import { Link, Navigate } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';
import { useState, type SyntheticEvent } from 'react';

import { Button, Input, Label } from '@shigo-ui/ui';

import { readShigoAppConfig } from '../../lib/config';
import { dashboardT } from '../../lib/messages';
import { useShigoSession } from '../../lib/useShigoSession';
import { createShigoSession } from './api';
import { AuthPanel, AuthStep } from './components/AuthLayout';
import { AuthPasswordInput } from './components/AuthPasswordInput';
import { DashboardSelectionStep } from '../dashboards/DashboardSelectionStep';
import {
  resolvePostLoginDestination,
  ShigoDashboardContext,
  type ShigoDashboardDestination,
} from './dashboardDestination';

export function LoginPage() {
  const { isAuthenticated, session, setSession } = useShigoSession();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const config = readShigoAppConfig();

  if (isAuthenticated && session) {
    const destination = resolvePostLoginDestination(session.user);
    if (destination.context === ShigoDashboardContext.Selection) {
      return <DashboardSelectionStep />;
    }
    return <DashboardDestinationNavigate destination={destination} />;
  }

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const emailValue = formData.get('email');
    const passwordValue = formData.get('password');
    const email = typeof emailValue === 'string' ? emailValue.trim() : '';
    const password = typeof passwordValue === 'string' ? passwordValue : '';

    setError('');
    setIsSubmitting(true);

    try {
      const session = await createShigoSession({ email, password }, config);
      setSession(session);
    } catch {
      setError(dashboardT('auth.login.error'));
    } finally {
      setIsSubmitting(false);
    }
  }

  const submitLabel = isSubmitting
    ? dashboardT('auth.login.submitting')
    : dashboardT('auth.login.submit');

  return (
    <AuthPanel
      description={dashboardT('auth.login.description', { network: config.displayName })}
      step={AuthStep.Login}
      title={dashboardT('auth.login.title')}
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs" htmlFor="email">
            {dashboardT('auth.login.email')}
          </Label>
          <Input
            autoComplete="email"
            className="h-11 rounded-lg bg-transparent text-[15px]"
            id="email"
            name="email"
            placeholder={dashboardT('auth.login.emailPlaceholder')}
            required
            type="email"
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-3">
            <Label className="text-muted-foreground text-xs" htmlFor="password">
              {dashboardT('auth.login.password')}
            </Label>
            <Link
              className="text-primary text-xs font-medium underline-offset-4 hover:underline"
              to="/forgot-password"
            >
              {dashboardT('auth.login.forgotPassword')}
            </Link>
          </div>
          <AuthPasswordInput
            autoComplete="current-password"
            id="password"
            name="password"
            required
          />
        </div>
        {error ? (
          <p
            className="text-destructive rounded-lg border border-current/20 bg-current/5 px-3 py-2.5 text-sm"
            role="alert"
          >
            {error}
          </p>
        ) : null}
        <Button className="mt-2 h-11 w-full" disabled={isSubmitting} type="submit">
          {isSubmitting ? (
            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : null}
          {submitLabel}
        </Button>
      </form>
    </AuthPanel>
  );
}

function DashboardDestinationNavigate({ destination }: { destination: ShigoDashboardDestination }) {
  if (
    destination.context === ShigoDashboardContext.Publisher ||
    destination.context === ShigoDashboardContext.Agency
  ) {
    return <Navigate to={destination.to} params={destination.params} replace />;
  }
  return <Navigate to={destination.to} replace />;
}
